import React, { useContext, useState, useEffect } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import PDFUploader from 'components/UploaderAndDownloader/PDFUploader';
import BlobDownloader from 'components/UploaderAndDownloader/BlobDownloader';
import Button from 'components/Button/Button';
import Text from 'components/Text/Text';
import breadcrumbs from 'assets/img/breadcrumbs.svg';
import { createAppError } from 'utils/helpers';
import { postMediaFile } from 'backend/additionalInformation';
import style from './PDFSection.module.scss';

interface IPDFSection {
	onUploadPDF: (fileId: string) => void;
	onDeleteFile: () => void;
	onSizeError: () => void;
	IdForPDFUploader: string;
	filePath: Nullable<string>;
	maxFileSizeMB: number;
	isShowPDFUploader?: boolean;
}

const PDFSection: React.FC<IPDFSection> = ({
	onUploadPDF,
	onDeleteFile,
	onSizeError,
	IdForPDFUploader,
	filePath,
	maxFileSizeMB,
	isShowPDFUploader = true,
}) => {
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const [pdfFileName, setPdfFileName] = useState('');

	const handleFileLoad = async (pdfFile: File) => {
		showLoader();
		const formData = new FormData();
		formData.append('file', pdfFile, pdfFile.name);
		try {
			const response = await postMediaFile({ data: formData });
			onUploadPDF(response.data.fileId);
			setPdfFileName(pdfFile.name);
			hideLoader();
		} catch (err) {
			hideLoader();
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		}
	};

	const handleDeleteFile = () => {
		setPdfFileName('');
		onDeleteFile();
	};

	useEffect(() => {
		filePath && setPdfFileName('');
	}, [filePath]);

	return (
		<div className={style.container} data-test="PDFSection">
			{isShowPDFUploader ? (
				<div className={style.uploadBtn}>
					<PDFUploader
						onFileLoad={handleFileLoad}
						onSizeError={onSizeError}
						fileName={pdfFileName}
						id={IdForPDFUploader}
						label="uploadPdfLabel"
						maxFileSizeMB={maxFileSizeMB}
					/>
				</div>
			) : null}
			<div className={style.downloadBnt}>
				<BlobDownloader filePath={filePath} fileExtension="pdf" fileName="document" label="downloadPdfLabel" />
			</div>
			{isShowPDFUploader && (!!filePath || !!pdfFileName) && (
				<div className={style.deleteButtonWrapper}>
					<Button
						className={style.deleteButton}
						theme="secondary"
						icon={
							<svg className={style.deleteIcon}>
								<use xlinkHref={`${breadcrumbs}#bin`} />
							</svg>
						}
						onClick={handleDeleteFile}
						data-test="deleteButton">
						<Text translationNameSpace="form" translationKey="deleteButton" asFragment={true} />
					</Button>
				</div>
			)}
		</div>
	);
};

export default React.memo(PDFSection);
